import { cn } from '@/lib/utils';
import {
  CheckCircle as CheckCircleIcon,
  TriangleAlert as ExclamationTriangleIcon,
  Info as InformationCircleIcon,
  XCircle as XCircleIcon,
  X as XIcon,
} from 'lucide-react';

export type AlertBannerColor = 'blue' | 'red' | 'yellow' | 'green' | 'gray' | 'violet' | 'amber';
export type AlertBannerSeverity = 'info' | 'error' | 'warning' | 'success' | 'default';

const severityToColor: Record<AlertBannerSeverity, AlertBannerColor> = {
  info: 'blue',
  error: 'red',
  warning: 'yellow',
  success: 'green',
  default: 'gray',
} as const;

const colorClasses: Record<AlertBannerColor, string> = {
  blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
  red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
  green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
  gray: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
  violet: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800',
  amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
};

const textClasses: Record<AlertBannerColor, string> = {
  blue: 'text-blue-700 dark:text-blue-300',
  red: 'text-red-700 dark:text-red-300',
  yellow: 'text-yellow-700 dark:text-yellow-300',
  green: 'text-green-700 dark:text-green-300',
  gray: 'text-gray-700 dark:text-gray-300',
  violet: 'text-violet-700 dark:text-violet-300',
  amber: 'text-amber-700 dark:text-amber-300',
};

const iconClasses: Record<AlertBannerColor, string> = {
  blue: 'text-blue-400',
  red: 'text-red-400',
  yellow: 'text-yellow-400',
  green: 'text-green-400',
  gray: 'text-gray-400',
  violet: 'text-violet-400',
  amber: 'text-amber-400',
};

const icons: Record<AlertBannerColor, typeof InformationCircleIcon> = {
  blue: InformationCircleIcon,
  red: XCircleIcon,
  yellow: ExclamationTriangleIcon,
  green: CheckCircleIcon,
  gray: InformationCircleIcon,
  violet: InformationCircleIcon,
  amber: ExclamationTriangleIcon,
};

interface AlertBannerProps {
  color?: AlertBannerColor;
  severity?: AlertBannerSeverity;
  className?: string;
  title?: React.ReactNode;
  role?: string;
  onClose?: () => void;
  children: React.ReactNode;
}

/**
 * A banner that displays a message with an icon and a colored container.
 * @param color - The color of the alert banner.
 * @param severity - The `severity` of the alert banner (maps to color).
 * @param className
 * @param title - Optional bold heading above the message.
 * @param role - Defaults to `alert` so screen readers announce the message.
 * @param onClose - When provided, shows a dismiss button.
 * @param children
 */
export const AlertBanner: React.FC<AlertBannerProps> = ({
  color,
  severity,
  className,
  title,
  role = 'alert',
  onClose,
  children,
}) => {
  const resolvedColor = color ?? (severity ? severityToColor[severity] : 'blue');
  const Icon = icons[resolvedColor];

  return (
    <div
      className={cn('rounded-md p-4 border-1', colorClasses[resolvedColor], className)}
      role={role}
    >
      <div className="flex">
        <div className="shrink-0">
          <Icon className={cn('h-5 w-5', iconClasses[resolvedColor])} aria-hidden="true" />
        </div>
        <div className={cn('ml-3 text-sm', textClasses[resolvedColor])}>
          {title && <h3 className="font-medium">{title}</h3>}
          {title ? <div className="mt-2">{children}</div> : children}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className={cn('ml-auto mt-0.5 shrink-0 self-start pl-3', textClasses[resolvedColor])}
          >
            <XIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};
